import { useState, useEffect, useCallback } from 'react';
import { analyticsService } from '../services/analyticsService';

interface UseAnalyticsOptions {
  entityType: 'persona' | 'workflow' | 'artifactory';
  entityId?: string;
  dateRange?: '7d' | '30d' | '90d';
  autoFetch?: boolean;
}

function dateRangeToParams(range?: string): Record<string, string> | undefined {
  if (!range) return undefined;
  const days = parseInt(range);
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

export function useAnalytics({ entityType, entityId, dateRange, autoFetch = true }: UseAnalyticsOptions) {
  const [summary, setSummary] = useState<any>(null);
  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = dateRangeToParams(dateRange);
      const methodMap: Record<string, (p?: any) => Promise<any>> = {
        persona: (p) => analyticsService.getPersonaSummary(p),
        workflow: () => analyticsService.getWorkflowSummary(),
        artifactory: () => analyticsService.getArtifactorySummary(),
      };
      const fn = methodMap[entityType];
      if (fn) setSummary(await fn(params));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [entityType, dateRange]);

  const fetchDetail = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const methodMap: Record<string, (id: string) => Promise<any>> = {
        persona: (id) => analyticsService.getPersonaDetail(id),
        workflow: (id) => analyticsService.getWorkflowDetail(id),
        artifactory: (id) => analyticsService.getArtifactoryDetail(id),
      };
      const fn = methodMap[entityType];
      if (fn) setDetail(await fn(id));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [entityType]);

  useEffect(() => {
    if (autoFetch) fetchSummary();
  }, [autoFetch, fetchSummary]);

  useEffect(() => {
    if (entityId) fetchDetail(entityId);
  }, [entityId, fetchDetail]);

  return { summary, detail, loading, error, refresh: fetchSummary };
}
